/**
 * Column profile definitions for the bid spreadsheet grid.
 *
 * Extracted from the legacy @/sheet/bidColumns.jsx to sever
 * the dependency on global mutable state (bidStore.showRunning).
 *
 * Each profile describes a column's identity, default width,
 * header text, visibility, and editability.
 */

const LINE_RUNNING_COLUMN_ID_PREFIX = "line_item_running";

export interface ColumnProfile {
    id: string;
    headerText?: string;
    headerIcon?: string;
    type: string;
    width?: number;
    defaultWidth?: number;
    visible?: boolean;
    alwaysVisible?: boolean;
    nonEditable?: boolean;
    leftSticking?: boolean;
    sticky?: boolean;
    restricted?: boolean;
    isRunning?: boolean;
    hasRunning?: boolean;
    runningVisible?: boolean;
}

// ── Left-sticking columns (always pinned left) ─────────────────────────────

const leftStickingColumns: ColumnProfile[] = [
    {
        id: "_order",
        type: "text",
        width: 28,
        defaultWidth: 28,
        alwaysVisible: true,
        nonEditable: true,
        leftSticking: true,
    },
    {
        id: "custom_id",
        headerIcon: "sort",
        headerText: "ID",
        defaultWidth: 60,
        width: 60,
        type: "customText",
        leftSticking: true,
    },
    {
        id: "name",
        headerIcon: "sort",
        headerText: "Name",
        type: "customChevron",
        defaultWidth: 300,
        width: 300,
        alwaysVisible: true,
        leftSticking: true,
    },
];

// ── Middle columns (scrollable, editable) ──────────────────────────────────

const middleColumns: ColumnProfile[] = [
    {
        id: "is_internal",
        headerIcon: "localAtm",
        headerText: "Resource Type",
        type: "customDropdownResourceType",
    },
    {
        id: "internal_rate",
        headerIcon: "localOffer",
        headerText: "Internal Rate",
        type: "simpleFormula",
        restricted: true,
        hasRunning: false,
    },
    {
        id: "internal_markup",
        headerIcon: "moving",
        headerText: "Internal Markup %",
        type: "simpleFormulaPercentage",
        restricted: true,
        hasRunning: false,
    },
    {
        id: "vendor_currency",
        headerIcon: "localAtm",
        headerText: "Currency",
        type: "customDropdownCurrency",
        visible: true,
    },
    {
        id: "vendor_rate",
        headerIcon: "localOffer",
        headerText: "Rate",
        type: "dynamicRate",
        visible: true,
    },
    {
        id: "rate_type",
        headerIcon: "accessTime",
        headerText: "Rate Type",
        type: "customDropdownRateType",
        visible: true,
    },
    {
        id: "vendor_client_exchange_rate_saved",
        headerIcon: "localAtm",
        headerText: "FX",
        type: "customNumber",
        visible: false,
        nonEditable: true,
    },
    {
        id: "exchange_rate_buffer",
        headerIcon: "moving",
        headerText: "FX Buffer",
        type: "simpleFormula",
        visible: false,
        nonEditable: true,
    },
    {
        id: "client_rate",
        headerIcon: "localOffer",
        headerText: "Cost",
        type: "customNumber",
        visible: true,
        nonEditable: true,
    },
    {
        id: "budget_buffer",
        headerIcon: "moving",
        headerText: "Budget Buffer %",
        type: "simpleFormulaPercentage",
        visible: true,
    },
    {
        id: "budget_buffer_amount",
        headerIcon: "moving",
        headerText: "Budget Buffer $",
        type: "simpleFormula",
        visible: true,
        runningVisible: false,
    },
    {
        id: "client_rate_budget_buffered",
        headerIcon: "localOffer",
        headerText: "Budgeted Rate",
        type: "customNumber",
        visible: true,
        runningVisible: false,
        nonEditable: true,
    },
    {
        id: "markup",
        headerIcon: "moving",
        headerText: "Markup %",
        type: "simpleFormulaPercentage",
        visible: true,
        hasRunning: false,
    },
    {
        id: "markup_amount",
        headerIcon: "moving",
        headerText: "Markup $",
        type: "simpleFormula",
        visible: true,
        hasRunning: false,
    },
    {
        id: "client_rate_quoted",
        headerIcon: "moving",
        headerText: "Quoted Rate",
        type: "customNumber",
        visible: true,
        nonEditable: true,
        hasRunning: false,
    },
    {
        id: "days",
        headerIcon: "people",
        headerText: "Units",
        type: "simpleFormulaOneAsEmpty",
        visible: true,
    },
    {
        id: "quantity",
        headerIcon: "cart",
        headerText: "Qty",
        type: "simpleFormula",
        visible: true,
    },
    {
        id: "ot_1_5",
        headerIcon: "alarm",
        headerText: "OT 1.5",
        type: "simpleFormula",
    },
    {
        id: "ot_2_0",
        headerIcon: "alarm",
        headerText: "OT 2.0",
        type: "simpleFormula",
    },
    {
        id: "adjustment",
        headerIcon: "moving",
        headerText: "Adjustment",
        type: "simpleFormula",
        visible: true,
        hasRunning: false,
    },
    {
        id: "external_notes",
        headerIcon: "note",
        headerText: "External Notes",
        type: "customText",
        defaultWidth: 300,
        width: 300,
        visible: true,
        hasRunning: false,
    },
    {
        id: "internal_notes",
        headerIcon: "note",
        headerText: "Internal Notes",
        type: "customText",
        defaultWidth: 300,
        width: 300,
        visible: true,
        hasRunning: false,
    },
    {
        id: "internal_budget",
        headerIcon: "priceCheck",
        headerText: "Internal Cost",
        type: "customNumber",
        nonEditable: true,
        restricted: true,
        hasRunning: false,
    },
];

// ── Base columns (left + middle) ───────────────────────────────────────────

const baseColumns: ColumnProfile[] = [...leftStickingColumns, ...middleColumns];

// ── Right-sticking columns (pinned right, bid mode) ────────────────────────

const rightStickingColumns: ColumnProfile[] = [
    {
        id: "client_budget_buffered",
        headerIcon: "payments",
        headerText: "Budget Total",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        visible: true,
    },
    {
        id: "client_quoted",
        headerIcon: "paid",
        headerText: "Quoted Total",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        alwaysVisible: true,
    },
];

// ── Running columns (generated from base columns with hasRunning !== false) ─

const runningColumns: ColumnProfile[] = baseColumns
    .filter((col) => col.hasRunning !== false)
    .map((col) => ({
        ...col,
        isRunning: true,
        id: `${LINE_RUNNING_COLUMN_ID_PREFIX}.${col.id}`,
        visible: col.runningVisible === false ? false : col.visible,
    }));

// ── Right-sticking columns (pinned right, running mode) ────────────────────

const rightStickingRunningColumns: ColumnProfile[] = [
    {
        id: `${LINE_RUNNING_COLUMN_ID_PREFIX}.client_budget_buffered`,
        headerIcon: "run",
        headerText: "Running Budget",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        visible: true,
        isRunning: true,
    },
    {
        id: `${LINE_RUNNING_COLUMN_ID_PREFIX}.variance`,
        headerIcon: "run",
        headerText: "Variance",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        visible: true,
        isRunning: true,
    },
    {
        id: `${LINE_RUNNING_COLUMN_ID_PREFIX}.client_budget_buffered_original`,
        headerIcon: "payments",
        headerText: "Budget Total",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        visible: true,
        isRunning: true,
    },
    {
        id: `${LINE_RUNNING_COLUMN_ID_PREFIX}.client_quoted_original`,
        headerIcon: "paid",
        headerText: "Quoted Total",
        type: "customNumber",
        nonEditable: true,
        sticky: true,
        visible: true,
        isRunning: true,
        alwaysVisible: true,
    },
];

// ── Public API ─────────────────────────────────────────────────────────────

/** All column profiles across both modes. Used for the column visibility menu. */
export const allColumnProfiles: ColumnProfile[] = [
    ...leftStickingColumns,
    ...baseColumns,
    ...runningColumns,
    ...rightStickingColumns,
    ...rightStickingRunningColumns,
];

/**
 * Returns the column profiles for the given mode.
 * Pure function — no global state reads/writes.
 *
 * @param isRunning - Whether to return running-mode columns
 */
export function getColumnsForMode(isRunning: boolean): ColumnProfile[] {
    if (isRunning) {
        return [...runningColumns, ...rightStickingRunningColumns];
    }
    return [...baseColumns, ...rightStickingColumns];
}
